import { useEffect, useRef } from 'react';
import { AudioMotionAnalyzer } from 'audiomotion-analyzer';
import { useMedia } from './useMedia';
import { createLogger } from '@airhead-games/shared/utilities';
import { getColorStop } from '../styles/playerColors';
const logger = createLogger('useAudioVisualization');

interface UseAudioVisualizationProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

export function useAudioVisualization({
  containerRef,
  enabled = true,
}: UseAudioVisualizationProps) {
  const { webAudio, currentVolumes, isMuted: isGloballyMuted } = useMedia();
  const speechAudioMotionRef = useRef<AudioMotionAnalyzer | null>(null);
  const musicAudioMotionRef = useRef<AudioMotionAnalyzer | null>(null);
  const sfxAudioMotionRef = useRef<AudioMotionAnalyzer | null>(null);

  const SOLID_COLOR = getColorStop('neutral', '950');
  const TRANSPARENT_BG = `${getColorStop('neutral', '950')}00`;

  // Main effect for setting up the AudioMotionAnalyzers
  useEffect(() => {
    // Skip initialization if disabled
    if (!enabled) {
      return;
    }

    // Ensure we have the required elements
    if (!containerRef.current || !webAudio) {
      logger.warn('Missing required elements for visualization:', {
        container: !!containerRef.current,
        webAudio: !!webAudio,
      });
      return;
    }

    // Get required audio components
    const audioContext = webAudio.getContext();
    const speechAnalyzer = webAudio.getAnalyzer('speech');
    const musicAnalyzer = webAudio.getAnalyzer('music');
    const sfxAnalyzer = webAudio.getAnalyzer('sfx');

    if (!audioContext || !speechAnalyzer || !musicAnalyzer || !sfxAnalyzer) {
      logger.warn('Missing required audio components for visualization:', {
        context: audioContext ? 'available' : 'missing',
        speechAnalyzer: speechAnalyzer ? 'available' : 'missing',
        musicAnalyzer: musicAnalyzer ? 'available' : 'missing',
        sfxAnalyzer: sfxAnalyzer ? 'available' : 'missing',
      });
      return;
    }

    try {
      // Clean up any existing instances
      if (speechAudioMotionRef.current) {
        speechAudioMotionRef.current.destroy();
        speechAudioMotionRef.current = null;
      }
      if (musicAudioMotionRef.current) {
        musicAudioMotionRef.current.destroy();
        musicAudioMotionRef.current = null;
      }
      if (sfxAudioMotionRef.current) {
        sfxAudioMotionRef.current.destroy();
        sfxAudioMotionRef.current = null;
      }

      // Clear the container
      containerRef.current.innerHTML = '';

      // Create child containers for layered visualization
      const speechContainer = document.createElement('div');
      const musicContainer = document.createElement('div');
      const sfxContainer = document.createElement('div');

      // Add CSS classes for styling
      speechContainer.className = 'speech-visualization';
      musicContainer.className = 'music-visualization';
      sfxContainer.className = 'sfx-visualization';

      // Style containers for absolute positioning and layering
      const styleContainer = (container: HTMLElement, size: string) => {
        container.style.position = 'absolute';
        container.style.top = '50%';
        container.style.left = '50%';
        container.style.transform = 'translate(-50%, -50%)';
        container.style.width = size;
        container.style.height = size;
      };

      // Different sizes for each analyzer
      styleContainer(speechContainer, '100%'); // 64x64px
      styleContainer(musicContainer, '80%'); // 48x48px
      styleContainer(sfxContainer, '88%'); // ~56x56px

      // Set z-index for layering: bottom = 1, middle = 2, top = 3)
      musicContainer.style.zIndex = '1';
      sfxContainer.style.zIndex = '2';
      speechContainer.style.zIndex = '3';

      // Set opacity for overlaying
      musicContainer.style.opacity = '1';
      sfxContainer.style.opacity = '1';
      speechContainer.style.opacity = '1';

      // Append to main container
      containerRef.current.appendChild(sfxContainer);
      containerRef.current.appendChild(musicContainer);
      containerRef.current.appendChild(speechContainer);

      // Common options for all visualizations
      const commonOptions = {
        audioCtx: audioContext,
        connectSpeakers: false,
        mode: 10,
        gradientLeft: 'prism',
        gradientRight: 'prism',
        showPeaks: false,
        reflexRatio: 0.5,
        reflexAlpha: 1,
        reflexBright: 1,
        radial: true,
        spinSpeed: 0,
        channelLayout: 'dual-combined' as const,
        mirror: -1,
        showBgColor: false,
        overlay: true,
        radius: 0,
        showScaleX: false,
        showScaleY: false,
        fftSize: 256,
        barspace: 0,
        fillAlpha: 1,
        outlinebars: true,
        roundbars: true,
        sensitivity: 6,
        maxFreq: 1000,
        smoothing: 0.85,
      };

      // Create speech visualization
      speechAudioMotionRef.current = new AudioMotionAnalyzer(speechContainer, {
        ...commonOptions,
        source: speechAnalyzer,
      });

      // Create music visualization
      musicAudioMotionRef.current = new AudioMotionAnalyzer(musicContainer, {
        ...commonOptions,
        source: musicAnalyzer,
      });

      // Create SFX visualization
      sfxAudioMotionRef.current = new AudioMotionAnalyzer(sfxContainer, {
        ...commonOptions,
        source: sfxAnalyzer,
      });

      // Register gradients for speech (unmuted state)
      speechAudioMotionRef.current.registerGradient('unmutedColors', {
        bgColor: TRANSPARENT_BG,
        colorStops: [
          { color: getColorStop('amber', '400'), pos: 0 },
          { color: getColorStop('lime', '100'), pos: 0.2 },
          { color: getColorStop('lime', '300'), pos: 0.4 },
          { color: getColorStop('lime', '900'), pos: 1 },
        ],
      });

      // Register gradients for speech (muted state)
      speechAudioMotionRef.current.registerGradient('mutedColors', {
        bgColor: TRANSPARENT_BG,
        colorStops: [
          { color: getColorStop('neutral', '950'), pos: 0 },
          { color: getColorStop('red', '900'), pos: 0.64 },
          { color: getColorStop('red', '300'), pos: 0.88 },
          { color: getColorStop('red', '100'), pos: 1 },
        ],
      });

      // Register solid color gradients for each analyzer
      speechAudioMotionRef.current.registerGradient('speechSolid', {
        bgColor: TRANSPARENT_BG,
        colorStops: [{ color: SOLID_COLOR, pos: 0 }],
      });

      musicAudioMotionRef.current.registerGradient('musicSolid', {
        bgColor: TRANSPARENT_BG,
        colorStops: [{ color: SOLID_COLOR, pos: 0 }],
      });

      sfxAudioMotionRef.current.registerGradient('sfxSolid', {
        bgColor: TRANSPARENT_BG,
        colorStops: [{ color: SOLID_COLOR, pos: 0 }],
      });

      // Speech should stay dark-neutral; use solid neutral in all states.
      speechAudioMotionRef.current.setOptions({
        gradientLeft: 'speechSolid',
        gradientRight: 'speechSolid',
      });

      // Set gradients for music and SFX
      musicAudioMotionRef.current.setOptions({
        gradientLeft: 'musicSolid',
        gradientRight: 'musicSolid',
      });

      sfxAudioMotionRef.current.setOptions({
        gradientLeft: 'sfxSolid',
        gradientRight: 'sfxSolid',
      });

      logger.debug('All AudioMotionAnalyzers setup complete');
    } catch (err) {
      logger.error('AudioMotionAnalyzers setup failed:', err);
    }

    // Cleanup function
    const container = containerRef.current; // Capture for cleanup
    return () => {
      if (speechAudioMotionRef.current) {
        speechAudioMotionRef.current.destroy();
        speechAudioMotionRef.current = null;
      }
      if (musicAudioMotionRef.current) {
        musicAudioMotionRef.current.destroy();
        musicAudioMotionRef.current = null;
      }
      if (sfxAudioMotionRef.current) {
        sfxAudioMotionRef.current.destroy();
        sfxAudioMotionRef.current = null;
      }
      // Clear the container
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [
    enabled,
    containerRef,
    webAudio,
    currentVolumes,
    isGloballyMuted,
    SOLID_COLOR,
    TRANSPARENT_BG,
  ]);

  // Effect to control analyzer visibility and speech gradient based on volume levels
  useEffect(() => {
    // Skip volume control if disabled or analyzers don't exist
    if (!enabled) {
      return;
    }

    // Control music analyzer visibility
    if (musicAudioMotionRef.current) {
      const musicContainer = musicAudioMotionRef.current.canvas.parentElement;
      if (musicContainer) {
        musicContainer.style.display =
          currentVolumes.musicVolume === 0 ? 'none' : 'block';
      }
    }

    // Control SFX analyzer visibility
    if (sfxAudioMotionRef.current) {
      const sfxContainer = sfxAudioMotionRef.current.canvas.parentElement;
      if (sfxContainer) {
        sfxContainer.style.display =
          currentVolumes.sfxVolume === 0 ? 'none' : 'block';
      }
    }

    // Keep speech analyzer in dark-neutral regardless of mute state.
    if (speechAudioMotionRef.current) {
      speechAudioMotionRef.current.setOptions({
        gradientLeft: 'speechSolid',
        gradientRight: 'speechSolid',
      });
    }
  }, [
    enabled,
    currentVolumes.musicVolume,
    currentVolumes.sfxVolume,
    currentVolumes.speechVolume,
    isGloballyMuted,
  ]);

  return { speechAudioMotionRef, musicAudioMotionRef, sfxAudioMotionRef };
}
